/**
 * The API-backed data source: the same requests the POS pages issued before
 * Phase 3, moved behind the seam verbatim. Browser builds run on this and
 * behave exactly as before.
 */
import { api, qs } from '../api';
import type {
  BillTicketInput,
  CreateTicketInput,
  InvoiceDto,
  InvoiceRowDto,
  MenuItemDto,
  PosDataSource,
  StayDto,
  SyncDiagnostics,
  SyncStatus,
  TableDto,
  TicketDto,
} from './types';

export class RemoteDataSource implements PosDataSource {
  readonly mode = 'api' as const;

  getMenu(): Promise<MenuItemDto[]> {
    return api.get<MenuItemDto[]>('/api/menu?activeOnly=true');
  }

  listOpenTickets(): Promise<TicketDto[]> {
    return api.get<TicketDto[]>('/api/tickets?status=OPEN');
  }

  getTicket(id: string): Promise<TicketDto> {
    return api.get<TicketDto>(`/api/tickets/${id}`);
  }

  createTicket(input: CreateTicketInput): Promise<TicketDto> {
    return api.post<TicketDto>('/api/tickets', input);
  }

  addLine(ticketId: string, input: { menuItemId: string; quantity: number }): Promise<TicketDto> {
    return api.post<TicketDto>(`/api/tickets/${ticketId}/lines`, input);
  }

  removeLine(ticketId: string, lineId: string): Promise<TicketDto> {
    return api.delete<TicketDto>(`/api/tickets/${ticketId}/lines/${lineId}`);
  }

  billTicket(ticketId: string, input: BillTicketInput): Promise<InvoiceDto> {
    return api.post<InvoiceDto>(`/api/tickets/${ticketId}/bill`, input);
  }

  async voidTicket(ticketId: string, reason: string): Promise<void> {
    await api.post(`/api/tickets/${ticketId}/void`, { reason });
  }

  listTables(): Promise<TableDto[]> {
    return api.get<TableDto[]>('/api/tables');
  }

  listStays(): Promise<StayDto[]> {
    return api.get<StayDto[]>('/api/stays');
  }

  listInvoices(options: {
    kind?: 'FNB' | 'ROOM';
    q?: string;
    /** First and last day to include, as YYYY-MM-DD. */
    from?: string;
    to?: string;
    limit?: number;
  }): Promise<InvoiceRowDto[]> {
    return api.get<InvoiceRowDto[]>(`/api/invoices${qs({ kind: options.kind, q: options.q, from: options.from, to: options.to, limit: options.limit ?? 200 })}`);
  }

  getInvoice(id: string): Promise<InvoiceDto> {
    return api.get<InvoiceDto>(`/api/invoices/${id}`);
  }

  async voidInvoice(id: string, reason: string): Promise<void> {
    await api.post(`/api/invoices/${id}/void`, { reason });
  }

  pendingSyncCount(): Promise<number | null> {
    return Promise.resolve(null);
  }

  getSyncStatus(): Promise<SyncStatus | null> {
    return Promise.resolve(null);
  }

  syncNow(): Promise<void> {
    return Promise.resolve();
  }

  getSyncDiagnostics(): Promise<SyncDiagnostics | null> {
    return Promise.resolve(null);
  }
}
